import { Message } from "element-ui";
import axios from './client'
import { getProfile } from './store/utils'
import store from "./store/index"
const ACL = require('vf-lib/constants/access');
import { getToolbarButtons } from "./utils/toolbarButtonsConfig";

const JITSI_CONSTANTS = {
    defaultDisplayName: "Guest"
}

const canUserRecordVideo = async (email) => {
    if (!email || email.length <= 0)
        return;

    let data = null;
    try {
        const response = await axios.get("/api/facilitator/admin/dashboard");
        data = response.data.dashboardData;
    } catch (err) {
        return;
    }

    // Invalid admin dashboard response
    if (!data || data.length <= 0)
        return;

    const roomData = data[0];
    // Room not found
    if (!roomData)
        return;

    const facilitators = roomData.facilitators;
    // No facilitator data
    if (!facilitators)
        return;

    let ret = false;
    // Check if the user can record
    // Ignore the global "everyone" can-record filter
    facilitators.forEach((f) => {

        if (f.email === 'everyone')
            return;

        // Different User
        if (f.email !== email)
            return;

        const controls = f.controls;
        // Controls not available
        if (!controls)
            return;

        ret = ret || controls.includes(ACL.CAN_RECORD_VIDEO);
    });

    return ret;
}

const signInUser = async () => {
    const user = getProfile();
    const canRecordVideo = await canUserRecordVideo(user.email);
    // Use the local display name to sign in the user if present
    const localDisplayName = store.state.rooms.name;
    try {
        const response = await axios.post('/api/jaas/sign', {
            "id": user.email,
            "name": localDisplayName? localDisplayName: user.firstName,
            "email": user.email,
            "avatar": "",
        });
        return {
            token: response.data.token,
            canRecordVideo: canRecordVideo || false
        }
    } catch (err) {
        showMessage('Something went wrong, Please contact admin', 'error')
    }
}

const setMobileToolabarButtons = (buttons) => {
    if(isOnlyMobile()){
        let mobileButtons = ['microphone', 'camera','participants-pane', 'chat', 'raisehand', 'hangup', 'filmstrip', 'desktop', 'shareaudio']
        buttons =  buttons.filter((ele) => {
            return mobileButtons.indexOf(ele) !== -1;
        });
    }
    return buttons;
}

const initJitsi = async (roomName, lastN, isAudioMuted=false, isVideoMuted=false) => {
    const { token, canRecordVideo } = await signInUser()
    const buttons = getToolbarButtons(canRecordVideo);

    const toolbar_buttons = setMobileToolabarButtons(buttons);
    // Check if prejoin page is to be presented to the user,
    // In case of first time join, show the prejoin page
    // Else disable it
    let isShowPreJoinPage = true;

    try {
        const jitsiLocalStorage = JSON.parse(localStorage.getItem("jitsiLocalStorage"));
        isShowPreJoinPage = JSON.parse(localStorage.getItem('isShowPreJoinPage') || true)
        if(jitsiLocalStorage){
            const displayName = JSON.parse(jitsiLocalStorage["features/base/settings"] ||  "{}")["displayName"];
            const displayNameNotExist = typeof displayName !== "string" || displayName.trim().length === 0;
            isShowPreJoinPage = displayNameNotExist || isShowPreJoinPage;
        }
    } catch (e) {
        console.error(e);
        // Defaults to true
    }

    let jitsiDomain = process.env.VUE_APP_JITSI_DOMAIN;
    let jitsiOptions = {
        roomName: `${process.env.VUE_APP_JITSI_TENANT_NAME}/${roomName}`,
        width: "100%",
        height: "100%",
        parentNode: document.querySelector('#jitsi-iframe'),
        // reference: https://github.com/jitsi/jitsi-meet/blob/master/interface_config.js
        interfaceConfigOverwrite: {
            SHOW_JITSI_WATERMARK: false,
            SHOW_WATERMARK_FOR_GUESTS: false,
            APP_NAME: 'VideoFacilitator',
            CONNECTION_INDICATOR_DISABLED: true,
            DISABLE_FOCUS_INDICATOR: true,
            RECENT_LIST_ENABLED: false,
        },
        // reference: https://github.com/jitsi/jitsi-meet/blob/master/config.js
        configOverwrite: {
            hideAddRoomButton: true,
            requireDisplayName: !store.state.mainRoomConfig.currentRoom.hideUserNames ? true : false,
            hideDisplayName: store.state.mainRoomConfig.currentRoom.hideUserNames ? true : false,
            // channelLastN: lastN,
            startWithVideoMuted: isVideoMuted,
            prejoinPageEnabled: isShowPreJoinPage,
            subject: ' ',
            hideConferenceSubject: true,
            hideConferenceTimer: true,
            hideParticipantsStats: true,
            disableAudioLevels: true,
            enableNoisyMicDetection: false,
            enableDisplayNameInStats: true,
            defaultLocalDisplayName: JITSI_CONSTANTS.defaultDisplayName,
            disabledSounds: ['NOISY_AUDIO_INPUT_SOUND', 'TALK_WHILE_MUTED_SOUND'],
            disabledNotifications: ['toolbar.talkWhileMutedPopup', 'toolbar.noisyAudioInputTitle'],
            toolbarButtons: toolbar_buttons,
            toolbarConfig: {
                // The initial number of milliseconds for the toolbar buttons to be visible on screen.
                initialTimeout: 15000,
                // Moved from interfaceConfig.TOOLBAR_TIMEOUT
                // Number of milliseconds for the toolbar buttons to be visible on screen.
                timeout: 5000,
                // Moved from interfaceConfig.TOOLBAR_ALWAYS_VISIBLE
                // Whether toolbar should be always visible or should hide after x milliseconds.
                alwaysVisible: false,
                // Indicates whether the toolbar should still autohide when chat is open
                autoHideWhileChatIsOpen: false,
            },
            buttonsWithNotifyClick:[
                {
                    key: 'chat',
                    preventExecution: false
                },
                {
                    key: 'participants-pane',
                    preventExecution: false
                }
            ],
            conferenceInfo: {
                     alwaysVisible: ['recording', 'local-recording', 'raised-hands-count'],
                     autoHide: [
                         'video-quality'
                     ]
            },
            constraints: {
                video: {
                    height: {
                        ideal: 1080,
                        max: 1080,
                        min: 180
                    },
                    width: {
                        ideal: 1920,
                        max: 1920,
                        min: 320
                    }
                }
            },
            p2p: {
                enabled: false,
            },
            startAudioMuted: 4
        },
        jwt: token
    };

    // overWrite startWithAudioMuted only if it is true, let jitsi figure out otherwise
    if (isAudioMuted)
        jitsiOptions.configOverwrite.startWithAudioMuted = isAudioMuted;

    // Reset the prejoin page status to disable it
    localStorage.setItem('isShowPreJoinPage', false);

    // eslint-disable-next-line
    return {
        // reference: https://developer.8x8.com/jaas/docs/iframe-api-integration
        // eslint-disable-next-line no-undef
        jitsiApi: new JitsiMeetExternalAPI(jitsiDomain, jitsiOptions),
        isPreJoinEnabled: isShowPreJoinPage
    };
}

const showMessage = (message, type) => {
    Message({
        message: message,
        type: type,
        center: true,
        duration: 3000,
    });
}

const isMobile = () => {
    const { userAgent } = navigator;
    if (userAgent.match(/Android/i)) {
        return true;
    } else if (userAgent.match(/iP(ad|hone|od)/i)) {
        return true;
    } else {
        return false;
    }
}

const isOnlyMobile = () => {
    const { userAgent } = navigator;
    if (userAgent.match(/Android/i)) {
        return true;
    } else if (userAgent.match(/iP(hone|od)/i)) {
        return true;
    } else {
        return false;
    }
}

const playAudio = () => {
    let sound = new Audio('/room-enter.wav');
    sound.play();
}

/**
 * Get Socket message payload
 * @param {*} payload
 * @returns {Object}
 */
const getSocketPayload = (payload) => {
    return {
        ...payload
    }
}

export {
    initJitsi,
    showMessage,
    isMobile,
    playAudio,
    getSocketPayload,
    setMobileToolabarButtons,
    isOnlyMobile,
    JITSI_CONSTANTS
}
